/**
 * MoneyOnFire.js — Late fees, interest charges, and wasted money card
 *
 * Red accent card that highlights money being burned on:
 *   - Credit card late fees
 *   - Interest charges
 *   - Overdraft fees
 *   - Any other avoidable financial waste
 *
 * Hidden when there are no fire items (don't show an empty warning).
 *
 * Props:
 *   items   — Array of { name, amount, type } objects
 *   total   — Pre-calculated total (or computed from items)
 *
 * Phase 3: Static display from hardcoded/manual data
 * Phase 4: Auto-populated from Monarch transaction matching
 */
import React, { useState } from 'react';
import { formatCurrency } from '../utils/format';
import theme from '../utils/theme';

/**
 * MoneyOnFire card — shows financial waste items.
 * Returns null if no items (card is hidden, not empty).
 */
export default function MoneyOnFire({ items = [], total }) {
  const [expanded, setExpanded] = useState(false);

  // Don't render if nothing is on fire
  if (!items || items.length === 0) return null;

  const computedTotal = total || items.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <div className="fire-card">
      {/* Header with total */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer' }}
      >
        <h3>
          <span role="img" aria-label="fire">🔥</span>
          Money on Fire
          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', opacity: 0.6 }}>
            {expanded ? '▲' : '▼'}
          </span>
        </h3>
        <div className="fire-total" style={{ marginTop: '0.25rem' }}>
          {formatCurrency(computedTotal)}<span style={{ fontSize: '0.8rem', fontWeight: 400 }}>/mo</span>
        </div>
        <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: '0.15rem' }}>
          {items.length} avoidable charge{items.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Expandable item list */}
      {expanded && (
        <div style={{ marginTop: '0.75rem', borderTop: `1px solid ${theme.fireBorder}`, paddingTop: '0.5rem' }}>
          {items.map((item, i) => (
            <div key={i} className="bill-row" style={{ borderColor: theme.fireBorder }}>
              <div className="bill-info">
                <div className="bill-name" style={{ color: theme.danger }}>{item.name}</div>
                <div className="bill-due">
                  {item.type === 'late_fee' && 'Late fee'}
                  {item.type === 'interest' && 'Interest charge'}
                  {item.type === 'overdraft' && 'Overdraft fee'}
                  {item.type === 'other' && 'Avoidable charge'}
                  {!item.type && 'Fee/interest'}
                </div>
              </div>
              <div className="bill-amount" style={{ color: theme.danger }}>
                {formatCurrency(item.amount)}
              </div>
            </div>
          ))}

          {/* Annualized impact */}
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem',
            background: `${theme.danger}14`,
            borderRadius: '6px',
            fontSize: '0.75rem',
            color: theme.danger,
            textAlign: 'center',
          }}>
            That's <strong>{formatCurrency(computedTotal * 12, false)}/year</strong> in avoidable charges
          </div>
        </div>
      )}
    </div>
  );
}
